/**
 * The page a verification link lands on. POSTs the token once on mount and
 * reports. A spent link ("already_verified") is good news, not an error;
 * anything else offers a resend, since the usual cause is a link older than
 * three days.
 */
import { useEffect, useRef, useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import AuthCard, { AuthMessage, INPUT_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS } from '../components/AuthCard';

type Outcome =
  | { state: 'checking' }
  | { state: 'verified'; message: string }
  | { state: 'already'; message: string }
  | { state: 'failed'; message: string };

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [outcome, setOutcome] = useState<Outcome>({ state: 'checking' });
  const [email, setEmail] = useState('');
  const [resent, setResent] = useState('');
  // React 18 strict mode mounts twice in dev; the token is single-use, so the
  // second POST would report "already used" over a success.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!token) {
      setOutcome({ state: 'failed', message: 'This verification link is missing its token.' });
      return;
    }
    api
      .verifyEmail(token)
      .then(({ message }) => setOutcome({ state: 'verified', message }))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Verification failed.';
        const code = (err as { code?: string })?.code;
        setOutcome({ state: code === 'already_verified' ? 'already' : 'failed', message });
      });
  }, [token]);

  const handleResend = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const { message } = await api.resendVerification(email);
      setResent(message);
    } catch (err) {
      setResent(err instanceof Error ? err.message : 'Could not resend.');
    }
  };

  return (
    <AuthCard title="Verify email">
      {outcome.state === 'checking' && (
        <p className="text-center text-sm text-gray-600 dark:text-gray-300">Checking your link...</p>
      )}
      {(outcome.state === 'verified' || outcome.state === 'already') && (
        <>
          <AuthMessage tone="success">{outcome.message}</AuthMessage>
          {/* SELF-SERVE: a verified address is the whole bar for the free plan,
              so the next step is to sign in and open a room — not to wait. The
              server's message says "You're in" when that is what happened. */}
          {outcome.state === 'verified' && /you're in/i.test(outcome.message) && (
            <Link
              to="/login"
              className="block w-full text-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium"
            >
              Sign in and open your first recon
            </Link>
          )}
        </>
      )}
      {outcome.state === 'failed' && (
        <>
          <AuthMessage tone="error">{outcome.message}</AuthMessage>
          {resent ? (
            <AuthMessage tone="success">{resent}</AuthMessage>
          ) : (
            <form onSubmit={handleResend} className="space-y-4">
              <div>
                <label htmlFor="email" className={LABEL_CLASS}>
                  Send a new link to
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={INPUT_CLASS}
                />
              </div>
              <button type="submit" className={PRIMARY_BUTTON_CLASS}>
                Resend verification email
              </button>
            </form>
          )}
        </>
      )}
      <p className="text-center text-sm text-gray-600 dark:text-gray-300">
        <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:text-blue-500">
          Go to sign in
        </Link>
      </p>
    </AuthCard>
  );
}
